import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const INITIAL = {
  name_en: "", name_it: "", internal_ref: "", country: "", region: "", province: "", city: "",
  address: "", latitude: "", longitude: "", category_id: "", property_type: "", status: "draft",
  price: "", currency: "EUR", price_visibility: "visible", description_en: "", description_it: "",
  highlights_en: "", highlights_it: "", living_area: "", land_area: "", bedrooms: "", bathrooms: "",
  parking_spaces: "", construction_year: "", renovation_year: "", featured_image: "",
  images: [], maps_embed_url: "", maps_studio_url: "", assigned_users: [], assigned_groups: [],
};

export default function PropertyFormPage() {
  const { adminAxios } = useAdminAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(INITIAL);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [newImage, setNewImage] = useState("");
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    const loads = [
      adminAxios.get("/admin/categories").then(r => setCategories(r.data)),
      adminAxios.get("/admin/users").then(r => setUsers(r.data)),
    ];
    if (isEdit) {
      loads.push(adminAxios.get(`/admin/properties/${id}`).then(({ data }) => {
        setForm({ ...INITIAL, ...data, latitude: data.latitude ?? "", longitude: data.longitude ?? "", price: data.price ?? "", living_area: data.living_area ?? "", land_area: data.land_area ?? "", bedrooms: data.bedrooms ?? "", bathrooms: data.bathrooms ?? "", parking_spaces: data.parking_spaces ?? "", construction_year: data.construction_year ?? "", renovation_year: data.renovation_year ?? "" });
      }));
    }
    Promise.all(loads).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const f = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

  const addImage = () => {
    if (newImage.trim()) { setForm(prev => ({ ...prev, images: [...prev.images, newImage.trim()] })); setNewImage(""); }
  };
  const removeImage = (i) => setForm(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));
  const toggleUser = (uid) => setForm(prev => ({ ...prev, assigned_users: prev.assigned_users.includes(uid) ? prev.assigned_users.filter(u => u !== uid) : [...prev.assigned_users, uid] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        price: form.price ? parseFloat(form.price) : null,
        living_area: form.living_area ? parseFloat(form.living_area) : null,
        land_area: form.land_area ? parseFloat(form.land_area) : null,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        parking_spaces: form.parking_spaces ? parseInt(form.parking_spaces) : null,
        construction_year: form.construction_year ? parseInt(form.construction_year) : null,
        renovation_year: form.renovation_year ? parseInt(form.renovation_year) : null,
        category_id: form.category_id || null,
      };
      if (isEdit) await adminAxios.put(`/admin/properties/${id}`, payload);
      else await adminAxios.post("/admin/properties", payload);
      navigate("/sp-command-center/properties");
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const tabs = ["basic", "details", "content", "media", "visibility"];

  if (loading) return <div className="p-8 text-zinc-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/sp-command-center/properties")} className="text-zinc-400 hover:text-zinc-700 flex items-center gap-2 text-sm transition-colors">
            <ArrowLeft size={14} strokeWidth={1.5} /> Properties
          </button>
          <div className="w-px h-4 bg-zinc-200" />
          <h1 className="text-lg font-semibold text-zinc-900">{isEdit ? "Edit Property" : "Add Property"}</h1>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-property-btn" className="bg-[#C8102E] hover:bg-[#A50D25] text-white text-xs tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50">
          {saving ? "Saving..." : "Save Property"}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-zinc-200 px-8">
        <div className="flex gap-0">
          {tabs.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`px-5 py-3 text-xs font-medium tracking-wider uppercase border-b-2 transition-colors capitalize ${activeTab === t ? "border-[#C8102E] text-[#C8102E]" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={save} className="p-8 max-w-4xl">
        {/* BASIC */}
        {activeTab === "basic" && (
          <div className="space-y-5 bg-white border border-zinc-200 p-6">
            <SectionTitle>Property Identity</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <FInput label="Property Name (EN) *" value={form.name_en} onChange={f("name_en")} required />
              <FInput label="Property Name (IT)" value={form.name_it} onChange={f("name_it")} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FInput label="Internal Reference" value={form.internal_ref} onChange={f("internal_ref")} placeholder="REF-001" />
              <FSelect label="Category" value={form.category_id} onChange={f("category_id")} options={[{value:"",label:"Select..."},...categories.map(c=>({value:c.id,label:c.name_en}))]} />
              <FInput label="Property Type" value={form.property_type} onChange={f("property_type")} placeholder="Villa, Hotel..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FSelect label="Status" value={form.status} onChange={f("status")} options={["draft","published","private","archived","sold"].map(s=>({value:s,label:s}))} />
              <FSelect label="Price Visibility" value={form.price_visibility} onChange={f("price_visibility")} options={["visible","on_request","hidden"].map(s=>({value:s,label:s}))} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <FInput label="Price" value={form.price} onChange={f("price")} type="number" placeholder="0" />
              <FSelect label="Currency" value={form.currency} onChange={f("currency")} options={["EUR","USD","GBP","CHF"].map(c=>({value:c,label:c}))} />
            </div>
          </div>
        )}

        {/* DETAILS */}
        {activeTab === "details" && (
          <div className="space-y-5 bg-white border border-zinc-200 p-6">
            <SectionTitle>Location</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <FInput label="Country" value={form.country} onChange={f("country")} />
              <FInput label="Region" value={form.region} onChange={f("region")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FInput label="Province" value={form.province} onChange={f("province")} />
              <FInput label="City" value={form.city} onChange={f("city")} />
            </div>
            <FInput label="Address" value={form.address} onChange={f("address")} />
            <div className="grid grid-cols-2 gap-4">
              <FInput label="Latitude" value={form.latitude} onChange={f("latitude")} type="number" placeholder="41.9028" />
              <FInput label="Longitude" value={form.longitude} onChange={f("longitude")} type="number" placeholder="12.4964" />
            </div>
            <SectionTitle>Property Metrics</SectionTitle>
            <div className="grid grid-cols-3 gap-4">
              <FInput label="Living Area (m²)" value={form.living_area} onChange={f("living_area")} type="number" />
              <FInput label="Land Area (m²)" value={form.land_area} onChange={f("land_area")} type="number" />
              <FInput label="Bedrooms" value={form.bedrooms} onChange={f("bedrooms")} type="number" />
              <FInput label="Bathrooms" value={form.bathrooms} onChange={f("bathrooms")} type="number" />
              <FInput label="Parking Spaces" value={form.parking_spaces} onChange={f("parking_spaces")} type="number" />
              <FInput label="Construction Year" value={form.construction_year} onChange={f("construction_year")} type="number" />
              <FInput label="Renovation Year" value={form.renovation_year} onChange={f("renovation_year")} type="number" />
            </div>
            <SectionTitle>Maps</SectionTitle>
            <FInput label="Google Maps Embed URL" value={form.maps_embed_url} onChange={f("maps_embed_url")} placeholder="https://www.google.com/maps/embed?..." />
            <FInput label="Google Maps Studio URL" value={form.maps_studio_url} onChange={f("maps_studio_url")} placeholder="https://mymaps.google.com/..." />
          </div>
        )}

        {/* CONTENT */}
        {activeTab === "content" && (
          <div className="space-y-5 bg-white border border-zinc-200 p-6">
            <SectionTitle>Description</SectionTitle>
            <FArea label="Description (EN)" value={form.description_en} onChange={f("description_en")} rows={5} />
            <FArea label="Description (IT)" value={form.description_it} onChange={f("description_it")} rows={5} />
            <SectionTitle>Investment Highlights</SectionTitle>
            <FArea label="Investment Highlights (EN)" value={form.highlights_en} onChange={f("highlights_en")} rows={5} />
            <FArea label="Investment Highlights (IT)" value={form.highlights_it} onChange={f("highlights_it")} rows={5} />
          </div>
        )}

        {/* MEDIA */}
        {activeTab === "media" && (
          <div className="space-y-5 bg-white border border-zinc-200 p-6">
            <SectionTitle>Featured Image</SectionTitle>
            <FInput label="Featured Image URL" value={form.featured_image} onChange={f("featured_image")} placeholder="https://..." />
            {form.featured_image && <img src={form.featured_image} alt="" className="h-32 object-cover" onError={e => e.target.style.display="none"} />}
            <SectionTitle>Gallery Images</SectionTitle>
            <div className="flex gap-3">
              <input value={newImage} onChange={e => setNewImage(e.target.value)} onKeyPress={e => e.key === "Enter" && (e.preventDefault(), addImage())} placeholder="Paste image URL..." className="flex-1 border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400" data-testid="add-image-url" />
              <button type="button" onClick={addImage} className="bg-zinc-800 text-white px-4 py-2 text-xs tracking-wider hover:bg-zinc-700 transition-colors"><Plus size={14} /></button>
            </div>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                {form.images.map((img, i) => (
                  <div key={img} className="relative group">
                    <img src={img} alt="" className="w-full h-24 object-cover" onError={e => e.target.style.opacity="0.3"} />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VISIBILITY */}
        {activeTab === "visibility" && (
          <div className="space-y-5 bg-white border border-zinc-200 p-6">
            <SectionTitle>Assign to Users</SectionTitle>
            <p className="text-xs text-zinc-400">Only assigned users can view this property. Leave empty to hide from all investors.</p>
            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto border border-zinc-100 p-3">
              {users.length === 0 ? (
                <p className="text-zinc-400 text-sm col-span-2">No users available. Create users first.</p>
              ) : users.map(u => (
                <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-zinc-50 cursor-pointer">
                  <input type="checkbox" checked={form.assigned_users.includes(u.id)} onChange={() => toggleUser(u.id)} className="w-4 h-4 accent-[#C8102E]" data-testid={`assign-user-${u.id}`} />
                  <div>
                    <div className="text-sm text-zinc-700">{u.first_name} {u.last_name}</div>
                    {u.company && <div className="text-xs text-zinc-400">{u.company}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider pt-2 border-t border-zinc-100 mt-2">{children}</h3>;
}
function FInput({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white" />
    </div>
  );
}
function FArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
      <textarea value={value ?? ""} onChange={e => onChange(e.target.value)} rows={rows} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-y bg-white" />
    </div>
  );
}
function FSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
      <select value={value ?? ""} onChange={e => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 bg-white">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
